import CountUp from "react-countup";
import { Props_SnippetStatistic } from "./Statistic.props";
import { CompileMarkdown } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler'
import { useId } from 'react';

export const parseStatConfig = (str: string) => {
    const stringToParse = str.trim();
    const decimalMatch = stringToParse.match(/([.,])(\d+)$/);
    const integerPart = decimalMatch ? stringToParse.slice(0, -decimalMatch[0].length) : stringToParse;
    const separator = integerPart.match(/[ .,]/)?.[0] || '';
    const parsedValue = parseFloat(integerPart.replace(/\D/g, '') + (decimalMatch ? `.${decimalMatch[2]}` : ''));

    return {
        isNumeric: !isNaN(parsedValue) && /\d/.test(stringToParse),
        end: parsedValue || 0,
        decimals: decimalMatch ? decimalMatch[2].length : 0,
        decimal: decimalMatch ? decimalMatch[1] : ',',
        separator,
    };
};

const Statistic = (props: Props_SnippetStatistic ) => {
    const snippetId = useId();
    const {
        heading,
        paragraph,
        animation,
        repeatable_statisticItems: items = [],
    } = props;

    const animationEnabled = animation?.enabled?.value ?? true;
    const headingContent = heading?.content?.value;
    const paragraphContent = paragraph?.text?.value;

    const baseDelay = 0.3;
    const delayIncrement = 0.15;

    const processedItems = items.map((item, index) => {
        const { value, prefix, suffix, description, icon } = item;
        const rawValue = value?.value ?? '';
        const config = parseStatConfig(rawValue);
        const delay = baseDelay + (index * delayIncrement);
        return {
            key: `snippet-statistic-${snippetId}-item-${index}`,
            rawValue,
            config,
            delay,
            icon,
            prefix,
            suffix,
            description,
        };
    });

    return (
        <section className="snippet-statistic">
            <div className="snippet-statistic__top">
                <div className="snippet-statistic__container">
                    {headingContent && (
                    <div className="snippet-statistic__heading">
                            {CompileMarkdown(headingContent)}
                        </div>
                    )}
                    {paragraphContent && (
                        <div className="snippet-statistic__intro-paragraph">
                            {CompileMarkdown(paragraphContent)}
                        </div>
                    )}
                </div>
            </div>
            <div className="snippet-statistic__bottom">
                <div className="snippet-statistic__container">
                    <ul className="snippet-statistic__items">
                        {processedItems.map(({ key, rawValue, config, delay, prefix, suffix, description, icon }) => (
                            <li key={key} className="snippet-statistic__item">
                                <div className="snippet-statistic__item-cream-box" aria-hidden="true" />
                                {icon?.value && (
                                    <div className="snippet-statistic__icon-container">
                                        <img
                                            src={icon.value}
                                            alt=''
                                            className="snippet-statistic__icon"
                                            loading="lazy"
                                            width="60"
                                            height="60"
                                        />
                                    </div>
                                )}
                                <span className="snippet-statistic__value" aria-hidden="true">
                                    {animationEnabled && config.isNumeric ? (
                                        <>
                                            {prefix?.value}
                                            <CountUp
                                                start={0}
                                                end={config.end}
                                                duration={2}
                                                delay={delay}
                                                decimals={config.decimals}
                                                decimal={config.decimal}
                                                separator={config.separator}
                                                enableScrollSpy
                                                scrollSpyOnce
                                                scrollSpyDelay={delay * 1000}
                                            >
                                                {({ countUpRef }) => <span ref={countUpRef} />}
                                            </CountUp>
                                            {suffix?.value}
                                        </>
                                    ) : (
                                        <>
                                            {prefix?.value}{rawValue}{suffix?.value}
                                        </>
                                    )}
                                </span>
                                <span className="snippet-statistic__value--sr">
                                    {prefix?.value}{rawValue}{suffix?.value}
                                </span>
                                {description?.value && (
                                    <span className="snippet-statistic__description">
                                        {CompileMarkdown(description.value)}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
};

export default Statistic;